import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import { formatNumber } from "../../utils";
import { useCompact } from "../../utils/hooks/useCompact";

export const DAY_COLORS: Record<number, string> = {
  0: "#DC4A4A",
  1: "#F7B733",
  2: "#f9c4d6",
  3: "#46B773",
  4: "#FF8C00",
  5: "#1E88E5",
  6: "#8E44AD",
};

// Performance palette for the per-branch view (bar vs its own target).
const PERFORMANCE_COLORS = {
  good: "#46B773", // at / above target
  warn: "#F7B733", // close (>= 85% of target)
  bad: "#DC4A4A", // below
};

const performanceColor = (amount: number, target?: number) => {
  if (!target || target <= 0) return PERFORMANCE_COLORS.good;
  if (amount >= target) return PERFORMANCE_COLORS.good;
  if (amount >= target * 0.85) return PERFORMANCE_COLORS.warn;
  return PERFORMANCE_COLORS.bad;
};

interface SalesChartProps {
  readonly data: {
    readonly date: string;
    readonly amount: number;
    readonly dateIndex: number;
    readonly target?: number;
  }[];
  readonly badge?: string;
  readonly saleTarget?: number;
  /** "performance" colours each bar vs its target (per-branch view). */
  readonly colorMode?: "palette" | "performance";
  /** Per-bar value labels on top of each bar (off for the HO charts per design). */
  readonly showValueLabels?: boolean;
  /** Tooltip/label suffix unit, e.g. "บาท" for sales. */
  readonly valueUnit?: string;
}

export default function SalesBarChart({
  data,
  badge,
  saleTarget,
  colorMode = "palette",
  showValueLabels = true,
  valueUnit,
}: SalesChartProps) {
  const theme = useTheme();
  const isCompact = useCompact();
  const hasSaleTarget = typeof saleTarget === "number" && saleTarget > 0;

  return (
    <>
      {hasSaleTarget && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 18,
              borderTop: "2px dashed #3F51B5",
            }}
          />
          <Typography variant="caption" sx={{ color: "#3F51B5" }}>
            เป้ายอดขาย
          </Typography>
        </Box>
      )}
      <ResponsiveContainer width="100%" height={isCompact ? 220 : 300}>
        <BarChart
          data={data}
          barCategoryGap={20}
          margin={{ top: 24, right: 12, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            padding={{ left: 12, right: 12 }}
          />
          <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
          <Tooltip
            formatter={(value: number | string) => [
              `฿${formatNumber(value)}${valueUnit ? ` ${valueUnit}` : ""}`,
              "",
            ]}
            separator=""
            labelStyle={{ fontWeight: 600 }}
          />
          {hasSaleTarget && (
            <ReferenceLine
              y={saleTarget}
              stroke="#3F51B5"
              strokeDasharray="5 5"
            />
          )}
          <Bar
            dataKey="amount"
            radius={[6, 6, 0, 0]}
            fill="#888"
            isAnimationActive={true}
          >
            {data.map((item) => (
              <Cell
                key={item.date}
                fill={
                  colorMode === "performance"
                    ? performanceColor(item.amount, saleTarget ?? item.target)
                    : DAY_COLORS[item.dateIndex]
                }
              />
            ))}
            {showValueLabels && (
              <LabelList
                dataKey="amount"
                position="top"
                fill={theme.palette.text.primary}
                offset={6}
                style={{ fontSize: 10, fontWeight: 600 }}
                formatter={(value: unknown) => {
                  const numericValue =
                    typeof value === "number" || typeof value === "string"
                      ? value
                      : null;

                  return `฿${formatNumber(numericValue)}`;
                }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {badge && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: DAY_COLORS[data[0].dateIndex],
              borderRadius: "2px",
            }}
          />
          <Typography variant="body2">{badge}</Typography>
        </Box>
      )}
    </>
  );
}
